import { useEffect, useState, useCallback } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/contexts/AuthContext";
import { Lock, Clock, ArrowDownToLine, ArrowUpFromLine, X, AlertCircle, CalendarClock, Plus, Unlock, FolderClock, Wallet } from "lucide-react";
import RetirementMonthlyBanner from "@/components/RetirementMonthlyBanner";
import DashboardBanners from "@/components/DashboardBanners";
import JobLossVault from "@/components/JobLossVault";
import RetirementToggle from "@/components/RetirementToggle";
import CarVaultsSection from "@/components/CarVaultsSection";
import BillLocksSection from "@/components/BillLocksSection";
import BillLockReadyBanner from "@/components/BillLockReadyBanner";
import TimeLockDialog from "@/components/TimeLockDialog";
import PreorderCard from "@/components/PreorderCard";
import Quick15Pill from "@/components/Quick15Pill";
import { AutofillToggle, useAutofillRules } from "@/components/VaultControls";
import ReferralCard from "@/components/ReferralCard";
import MandatorySweepCard from "@/components/MandatorySweepCard";

function fmtTimer(secs) {
  if (secs <= 0) return "00:00:00";
  const h = Math.floor(secs / 3600).toString().padStart(2, "0");
  const m = Math.floor((secs % 3600) / 60).toString().padStart(2, "0");
  const s = Math.floor(secs % 60).toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function Countdown({ targetIso, label, big = false }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const secs = Math.max(0, Math.floor((new Date(targetIso).getTime() - now) / 1000));
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">{label}</div>
      <div className={`font-mono text-ink mt-1 ${big ? "text-3xl" : "text-2xl"}`} data-testid="countdown">{fmtTimer(secs)}</div>
    </div>
  );
}

function DepositDialog({ vault, onClose, onDone, savingsMin, savingsMax, retirementMin, retirementMax }) {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const isSavings = vault.type === "beneficiary";
  const isRetirement = vault.type === "retirement";
  const min = isRetirement ? retirementMin : isSavings ? savingsMin : 1;
  const max = isRetirement ? retirementMax : isSavings ? savingsMax : null;
  const limitLabel = isRetirement
    ? `Untouchable Retirement deposits: $${retirementMin}–$${retirementMax} per transaction.`
    : isSavings
      ? `Saving Goals deposits: $${savingsMin}–$${savingsMax} per transaction.`
      : null;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.post(`/vaults/${vault.id}/deposit`, { amount: parseFloat(amount) });
      onDone();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="deposit-dialog">
      <div className="bg-cream rounded-2xl border border-sand max-w-md w-full p-8">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Deposit via ACH</div>
          <button onClick={onClose} className="text-moss hover:text-ink" data-testid="deposit-close"><X className="w-4 h-4" /></button>
        </div>
        <h3 className="font-serif text-3xl mt-3">Add funds to {vault.name}</h3>
        {limitLabel && (
          <p className="text-xs text-sage mt-2" data-testid="deposit-limit-line">{limitLabel}</p>
        )}
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Amount (USD)</label>
            <input type="number" step="0.01"
              min={min} max={max || undefined}
              required value={amount} onChange={(e) => setAmount(e.target.value)}
              className="mt-2 w-full bg-white border border-sand rounded-lg px-4 py-3 font-mono text-lg focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
              placeholder={`${min}.00`} data-testid="deposit-amount" autoFocus />
          </div>
          {error && <div className="text-sm text-clay" data-testid="deposit-error">{error}</div>}
          <button type="submit" disabled={busy}
            className="w-full bg-forest text-white rounded-lg px-6 py-3.5 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors"
            data-testid="deposit-submit">
            {busy ? "Transferring…" : "Confirm deposit"}
          </button>
        </form>
      </div>
    </div>
  );
}

function WithdrawDialog({ vault, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.post(`/vaults/${vault.id}/withdraw`, { amount: parseFloat(amount) });
      onDone();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="withdraw-dialog">
      <div className="bg-cream rounded-2xl border border-sand max-w-md w-full p-8">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-gold">10-day Withdraw Guard hold</div>
          <button onClick={onClose} className="text-moss hover:text-ink" data-testid="withdraw-close"><X className="w-4 h-4" /></button>
        </div>
        <h3 className="font-serif text-3xl mt-3">Request a withdrawal</h3>
        <p className="text-sm text-moss mt-3 leading-relaxed">
          Funds remain in the vault for 10 days. After that, you have 24 hours to confirm.
          You may cancel at any time during the hold.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Amount (USD)</label>
            <input type="number" step="0.01" min="1" max={vault.balance} required value={amount} onChange={(e) => setAmount(e.target.value)}
              className="mt-2 w-full bg-white border border-sand rounded-lg px-4 py-3 font-mono text-lg focus:ring-2 focus:ring-gold focus:border-transparent outline-none"
              placeholder="0.00" data-testid="withdraw-amount" autoFocus />
            <p className="mt-1.5 text-xs text-sage">Available: {fmtMoney(vault.balance)}</p>
          </div>
          {error && <div className="text-sm text-clay">{error}</div>}
          <button type="submit" disabled={busy}
            className="w-full bg-gold text-white rounded-lg px-6 py-3.5 font-medium hover:bg-gold-deep disabled:opacity-60 transition-colors"
            data-testid="withdraw-submit">
            {busy ? "Requesting…" : "Begin 10-day hold"}
          </button>
        </form>
      </div>
    </div>
  );
}

function TimeLocksSection({ timelocks, onNew, onRelease, onExtend, onMoved, onEarlyRelease }) {
  const [showArchive, setShowArchive] = useState(false);
  const active = timelocks.filter((t) => t.status === "LOCKED" || t.status === "UNLOCKED");
  const finished = timelocks.filter((t) => t.status === "WITHDRAWN");

  return (
    <section className="mt-10" data-testid="timelocks-section">
      <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Date Time Lock</div>
          <h2 className="font-serif text-2xl tracking-tight mt-1">Pick a date. Seal a sum.</h2>
          <p className="text-xs text-moss mt-1 max-w-xl">
            Deposit money you don&apos;t want to see again until a specific date — a down payment, a wedding, a sabbatical.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowArchive(true)}
            disabled={finished.length === 0}
            className="inline-flex items-center gap-2 border border-sand text-ink rounded-md px-3 py-2 text-xs font-medium hover:bg-linen disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="timelocks-archive-btn"
            title={finished.length === 0 ? "No finished time locks yet" : "View finished time locks"}>
            <FolderClock className="w-3.5 h-3.5" strokeWidth={1.5} />
            Finished
            <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-sand-light text-moss text-[9px] font-mono"
                  data-testid="timelocks-archive-count">{finished.length}</span>
          </button>
          <button onClick={onNew}
            className="bg-forest text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-forest-deep transition-colors inline-flex items-center gap-1.5"
            data-testid="new-timelock-btn">
            <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> New time lock
          </button>
        </div>
      </div>

      {active.length === 0 ? (
        <div className="border border-dashed border-sand rounded-xl p-6 text-center text-sm text-sage" data-testid="timelocks-empty">
          No active time locks. Create your first.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="timelocks-list">
          {active.map((t) => (
            <TimeLockCard key={t.id} t={t} onRelease={onRelease} onExtend={onExtend} onEarlyRelease={onEarlyRelease} />
          ))}
        </div>
      )}

      {showArchive && (
        <ArchiveDrawer finished={finished} onClose={() => setShowArchive(false)} />
      )}
    </section>
  );
}

function TimeLockCard({ t, onRelease, onExtend, onEarlyRelease }) {
  const unlockDt = new Date(t.unlock_date);
  const [confirmEarly, setConfirmEarly] = useState(false);
  return (
    <div className="bg-linen border border-sand rounded-xl p-5" data-testid={`timelock-${t.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">{t.label}</div>
          <div className="font-mono text-2xl text-ink mt-1">{fmtMoney(t.amount)}</div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${
          t.status === "LOCKED" ? "bg-clay-light text-clay" :
          t.status === "UNLOCKED" ? "bg-forest text-white" :
          "bg-ink text-white"
        }`} data-testid={`timelock-status-${t.id}`}>
          {t.status === "LOCKED" ? <Lock className="w-2.5 h-2.5" /> : <Unlock className="w-2.5 h-2.5" />}
          {t.status}
        </span>
      </div>
      <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1.5 text-xs text-moss">
          <CalendarClock className="w-3.5 h-3.5 text-sage" strokeWidth={1.5} />
          <span className="font-mono text-[11px]">{unlockDt.toLocaleString()}</span>
        </div>
        {t.status === "LOCKED" && (
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <Countdown targetIso={t.unlock_date} label="Unlocks in" />
            {t.early_release_available && !confirmEarly && (
              <button onClick={() => setConfirmEarly(true)}
                className="inline-flex items-center gap-1 bg-ink text-cream rounded-md px-3 py-1.5 text-xs font-medium hover:bg-ink-deep transition-colors"
                data-testid={`early-release-once-${t.id}`}>
                <Unlock className="w-3 h-3" strokeWidth={2} /> {t.early_release_kind === "sixty" || t.early_release_kind === "eighty" ? `Release ${fmtMoney(t.amount)} early · one time only` : "Release early · one time only"}
              </button>
            )}
            {t.early_release_available && confirmEarly && (
              <div className="flex items-center gap-2 flex-wrap justify-end" data-testid={`early-release-confirm-${t.id}`}>
                <span className="text-[11px] text-clay">{t.early_release_kind === "sixty" || t.early_release_kind === "eighty" ? `This is your only early ${fmtMoney(t.amount)} release, ever. Other locks stay sealed until their date.` : "This is your only early release, ever. After this, locks stay sealed until their date."}</span>
                <button onClick={() => { setConfirmEarly(false); onEarlyRelease(t.id); }}
                  className="bg-clay text-white rounded-md px-3 py-1.5 text-xs font-medium hover:opacity-90" data-testid={`early-release-confirm-btn-${t.id}`}>Yes, release {fmtMoney(t.amount)}</button>
                <button onClick={() => setConfirmEarly(false)} className="text-xs text-sage hover:text-ink" data-testid={`early-release-cancel-${t.id}`}>Cancel</button>
              </div>
            )}
            <button onClick={() => onExtend(t)}
              className="inline-flex items-center gap-1 border border-gold text-gold rounded-md px-3 py-1.5 text-xs font-medium hover:bg-gold hover:text-white transition-colors"
              data-testid={`extend-${t.id}`}>
              <CalendarClock className="w-3 h-3" strokeWidth={2} /> Extend
            </button>
          </div>
        )}
        {t.status === "UNLOCKED" && (
          <div className="flex items-center gap-2">
            <button onClick={() => onExtend(t)}
              className="inline-flex items-center gap-1 border border-gold text-gold rounded-lg px-4 py-2 text-sm font-medium hover:bg-gold hover:text-white transition-colors"
              data-testid={`extend-${t.id}`}>
              <Lock className="w-3.5 h-3.5" strokeWidth={2} /> Re-lock
            </button>
            <button onClick={() => onRelease(t.id)}
              className="bg-forest text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-forest-deep transition-colors"
              data-testid={`release-${t.id}`}>
              Release funds
            </button>
          </div>
        )}
      </div>
      {(t.extend_history && t.extend_history.length > 0) && (
        <div className="mt-3 text-[10px] text-sage font-mono">
          Extended {t.extend_history.length}× · last on {new Date(t.last_extended_at).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}

function ExtendTimeLockDialog({ t, onClose, onDone }) {
  const currentDt = new Date(t.unlock_date);
  // Default new date = current + 7 days (or +1 day if already past)
  const defaultNew = new Date(Math.max(Date.now(), currentDt.getTime()) + 7 * 86400 * 1000);
  const [dateStr, setDateStr] = useState(defaultNew.toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      // Include a noon time so it doesn't unlock at midnight local.
      const iso = new Date(dateStr + "T12:00:00").toISOString();
      await api.post(`/timelocks/${t.id}/extend`, { unlock_date: iso });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  const isReLock = t.status === "UNLOCKED";
  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
         data-testid="timelock-extend">
      <div className="bg-cream rounded-2xl border border-sand max-w-md w-full p-8">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-gold">
            {isReLock ? "Re-lock" : "Extend"}
          </div>
          <button onClick={onClose} className="text-moss hover:text-ink"
                  data-testid="timelock-extend-close"><X className="w-4 h-4" /></button>
        </div>
        <h3 className="font-serif text-2xl mt-3">{t.label}</h3>
        <p className="text-xs text-moss mt-2">
          {isReLock
            ? <>Currently unlocked. Pick a new later date to seal {fmtMoney(t.amount)} back up.</>
            : <>Currently unlocks {currentDt.toLocaleDateString()}. Pick a later date to push it out.</>}
        </p>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">New unlock date</label>
            <input type="date" required
              min={new Date(currentDt.getTime() + 86400 * 1000).toISOString().slice(0, 10)}
              value={dateStr} onChange={(e) => setDateStr(e.target.value)}
              className="mt-2 w-full bg-white border border-sand rounded-lg px-4 py-3 font-mono focus:ring-2 focus:ring-gold focus:border-transparent outline-none"
              data-testid="timelock-extend-date" autoFocus />
          </div>
          {err && <div className="text-sm text-clay" data-testid="timelock-extend-error">{err}</div>}
          <button type="submit" disabled={busy}
            className="w-full bg-ink text-white rounded-lg px-6 py-3 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors"
            data-testid="timelock-extend-submit">
            {busy ? "Sealing…" : (isReLock ? "Re-lock until this date" : "Extend to this date")}
          </button>
        </form>
      </div>
    </div>
  );
}


function ArchiveDrawer({ finished, onClose }) {
  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
         data-testid="timelocks-archive">
      <div className="bg-cream rounded-2xl border border-sand max-w-lg w-full p-8 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-gold">Archive</div>
            <h3 className="font-serif text-2xl mt-1">Finished time locks</h3>
          </div>
          <button onClick={onClose} className="text-moss hover:text-ink"
                  data-testid="timelocks-archive-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-sage mt-3">Every completed release, for the record.</p>

        <ul className="mt-6 space-y-3">
          {finished.map((t) => {
            const unlockDt = new Date(t.unlock_date);
            return (
              <li key={t.id} className="bg-linen border border-sand rounded-xl p-4 flex items-center justify-between gap-3"
                  data-testid={`timelock-archived-${t.id}`}>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage truncate">{t.label}</div>
                  <div className="font-mono text-lg text-ink mt-1">{fmtMoney(t.amount)}</div>
                  <div className="text-[11px] text-sage font-mono mt-0.5">Unlocked {unlockDt.toLocaleDateString()}</div>
                </div>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium uppercase tracking-wider bg-ink text-white">
                  <Unlock className="w-3 h-3" /> released
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}



export default function Dashboard() {
  const { user, refresh: refreshUser } = useAuth();
  const [vaults, setVaults] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [timelocks, setTimelocks] = useState([]);
  const [billLockedTotal, setBillLockedTotal] = useState(0);
  const [spendingAvail, setSpendingAvail] = useState(0);
  const [showAvail, setShowAvail] = useState(false);
  const [cfg, setCfg] = useState({ savings_min: 20, savings_max: 500, retirement_min: 20, retirement_max: 500, timelock_durations: [1, 3, 5, 10] });
  const [depositVault, setDepositVault] = useState(null);
  const [withdrawVault, setWithdrawVault] = useState(null);
  const [showTimeLock, setShowTimeLock] = useState(false);
  const [extendTL, setExtendTL] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState("");
  const autofill = useAutofillRules();

  const load = useCallback(async () => {
    const [v, w, t, c, bl] = await Promise.all([
      api.get("/vaults"), api.get("/withdrawals"), api.get("/timelocks"), api.get("/config"),
      api.get("/bill-locks").catch(() => ({ data: { bill_locks: [] } })),
    ]);
    setBillLockedTotal((bl.data.bill_locks || []).filter((b) => b.status === "ACTIVE").reduce((s, b) => s + b.locked_amount, 0));
    setSpendingAvail(Number(bl.data.spending_available) || 0);
    setVaults(v.data);
    setWithdrawals(w.data);
    setTimelocks(t.data);
    setCfg(c.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = setInterval(() => {
      Promise.all([api.get("/withdrawals"), api.get("/timelocks")])
        .then(([w, t]) => { setWithdrawals(w.data); setTimelocks(t.data); })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const retirement = vaults.find((v) => v.type === "retirement");

  const executeWithdrawal = async (id) => {
    setActionMsg("");
    try {
      await api.post(`/withdrawals/${id}/execute`);
      setActionMsg("Withdrawal completed via ACH.");
      load();
    } catch (e) {
      setActionMsg(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const cancelWithdrawal = async (id) => {
    try {
      await api.post(`/withdrawals/${id}/cancel`);
      load();
    } catch (e) {
      setActionMsg(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const earlyReleaseOnce = async (id) => {
    setActionMsg("");
    try {
      const { data } = await api.post(`/timelocks/${id}/early-release-once`);
      setActionMsg(`${fmtMoney(data.released)} released to Spending. Your one-time early release is now used.`);
      load(); refreshUser && refreshUser(); window.dispatchEvent(new Event("bill-locks:refresh"));
    } catch (e) {
      setActionMsg(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const releaseTimeLock = async (id) => {
    setActionMsg("");
    try {
      await api.post(`/timelocks/${id}/withdraw`);
      setActionMsg("Time-lock released via ACH.");
      load();
    } catch (e) {
      setActionMsg(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-6 py-20 font-mono text-moss">loading vaults…</div>
    </div>
  );

  const lockedTotal = timelocks
    .filter((t) => t.status !== "WITHDRAWN")
    .reduce((s, t) => s + t.amount, 0);
  const grand = vaults.reduce((s, v) => s + v.balance, 0) + lockedTotal + billLockedTotal;
  const available = spendingAvail;

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Your vaults</div>
            <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-1">Locked in, levelling up.</h1>
          </div>
          <div className="flex items-start gap-4">
            <Quick15Pill onDone={(m) => { setActionMsg(m); load(); refreshUser && refreshUser(); window.dispatchEvent(new Event("bill-locks:refresh")); }} />
            <button type="button" onClick={() => setShowAvail((v) => !v)} aria-pressed={showAvail}
                  className="text-right group rounded-xl -m-2 p-2 transition-colors hover:bg-linen" data-testid="total-toggle"
                  title={showAvail ? "Tap to see total" : "Tap to see what's actually available"}>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-sage flex items-center justify-end gap-1.5">
              {showAvail ? <><Wallet className="w-3 h-3" strokeWidth={2} /> Available now</> : "Total"}
              <span className="text-sage-mid normal-case tracking-normal font-normal group-hover:text-forest transition-colors">· tap</span>
            </div>
            <div className={`font-mono text-4xl sm:text-5xl lg:text-6xl leading-none mt-1 tabular-nums ${showAvail ? "text-forest" : "text-ink"}`}
                 data-testid="total-balance">
              {fmtMoney(showAvail ? available : grand)}
            </div>
            {showAvail && (
              <div className="mt-2 text-xs text-moss flex justify-end gap-3 flex-wrap" data-testid="available-breakdown">
                <span>Spending <strong className="font-mono text-ink">{fmtMoney(spendingAvail)}</strong></span>
                <span className="text-sage">{fmtMoney(grand - available > 0 ? grand - available : 0)} locked in bills & vaults</span>
              </div>
            )}
          </button>
          </div>
        </div>

        {actionMsg && (
          <div className="mb-6 px-4 py-3 rounded-lg bg-linen border border-sand text-sm text-ink flex items-center gap-2" data-testid="action-message">
            <AlertCircle className="w-4 h-4 text-forest" strokeWidth={1.5} />
            {actionMsg}
          </div>
        )}

        {user?.demo && <PreorderCard />}

        <BillLockReadyBanner />

        <RetirementMonthlyBanner />

        <ReferralCard />
        <div className="mt-6"><MandatorySweepCard /></div>


        <DashboardBanners />


        <div className="grid md:grid-cols-2 gap-4 mt-4" data-testid="vaults-grid">
          {retirement && (() => {
            const today = new Date().toISOString().slice(0,10);
            const unlockDate = user?.retirement_unlock_date;
            const unlocked = unlockDate && unlockDate <= today;
            const yearsLeft = unlockDate
              ? Math.max(0, Math.ceil((new Date(unlockDate) - new Date()) / (365.25 * 24 * 3600 * 1000)))
              : null;
            return (
            <div className="bg-linen border border-sand rounded-2xl p-6" data-testid="retirement-vault">
              <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${
                unlocked ? "bg-forest text-white" : "bg-clay-light text-clay"
              }`}>
                <Lock className="w-3 h-3" strokeWidth={2} />
                {unlocked ? "Unlocked" : "Locked until age 60"}
              </div>
              <h3 className="font-serif text-2xl mt-3">{retirement.name}</h3>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Balance</div>
                <div className="font-mono text-4xl text-ink mt-1" data-testid="retirement-balance">{fmtMoney(retirement.balance)}</div>
                <RetirementToggle />
                {unlockDate && (
                  <div className="text-[11px] text-sage mt-2" data-testid="retirement-unlock-info">
                    {unlocked
                      ? `Unlocked ${unlockDate}`
                      : <>Unlocks <span className="font-mono text-ink">{unlockDate}</span> · <span className="text-forest font-medium">~{yearsLeft} yr{yearsLeft === 1 ? "" : "s"}</span> to go</>}
                  </div>
                )}
              </div>
              <div className="mt-5 flex gap-2">
                <button onClick={() => setDepositVault(retirement)}
                  className="flex-1 bg-forest text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-forest-deep transition-colors inline-flex items-center justify-center gap-2"
                  data-testid="retirement-deposit-btn">
                  <ArrowDownToLine className="w-3.5 h-3.5" strokeWidth={1.5} /> Deposit
                </button>
                {unlocked ? (
                  <button onClick={() => setWithdrawVault(retirement)}
                    className="flex-1 bg-transparent border border-forest text-forest rounded-md px-4 py-2 text-sm font-medium hover:bg-forest hover:text-white transition-colors inline-flex items-center justify-center gap-2"
                    data-testid="retirement-withdraw-btn">
                    Withdraw
                  </button>
                ) : (
                  <button disabled title="Locked until age 60"
                    className="flex-1 bg-transparent border border-sand text-sage rounded-md px-4 py-2 text-sm font-medium cursor-not-allowed inline-flex items-center justify-center gap-2"
                    data-testid="retirement-withdraw-disabled">
                    <Lock className="w-3.5 h-3.5" strokeWidth={1.5} /> Locked
                  </button>
                )}
              </div>
              <div className="mt-3"><AutofillToggle vaultId={retirement.id} rule={autofill.rules[retirement.id]} onChanged={autofill.reload} onMsg={setActionMsg} /></div>
            </div>
            );
          })()}
        </div>

        <JobLossVault />


        <CarVaultsSection />


        <BillLocksSection onChanged={load} />

        {/* Date Time Locks — active only, archive drawer for finished */}
        <TimeLocksSection
          timelocks={timelocks}
          onNew={() => setShowTimeLock(true)}
          onRelease={releaseTimeLock}
          onEarlyRelease={earlyReleaseOnce}
          onExtend={(t) => setExtendTL(t)}
          onMoved={(m) => { setActionMsg(m); load(); refreshUser && refreshUser(); window.dispatchEvent(new Event("bill-locks:refresh")); }}
        />
      </div>

      {depositVault && <DepositDialog vault={depositVault} savingsMin={cfg.savings_min} savingsMax={cfg.savings_max} retirementMin={cfg.retirement_min} retirementMax={cfg.retirement_max} onClose={() => setDepositVault(null)} onDone={() => { setDepositVault(null); load(); }} />}
      {withdrawVault && <WithdrawDialog vault={withdrawVault} onClose={() => setWithdrawVault(null)} onDone={() => { setWithdrawVault(null); load(); }} />}
      {showTimeLock && <TimeLockDialog savingsMin={cfg.savings_min} savingsMax={cfg.savings_max} durations={cfg.timelock_durations || [1,3,5,10]} onClose={() => setShowTimeLock(false)} onDone={() => { setShowTimeLock(false); load(); }} />}
      {extendTL && <ExtendTimeLockDialog t={extendTL} onClose={() => setExtendTL(null)} onDone={() => { setExtendTL(null); load(); }} />}
    </div>
  );
}
